<script setup lang="ts">
import axios from 'axios';
import { computed, nextTick, onUnmounted, ref, watch, watchEffect } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import PdfLivePreview from '@/components/Tools/PdfLivePreview.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolHowToCard from '@/components/Tools/ToolHowToCard.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import ToolSliderField from '@/components/Tools/ToolSliderField.vue';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { usePdfPage } from '@/composables/usePdfPage';
import { usePdfUploadLimit } from '@/composables/usePdfUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { dashboard } from '@/routes';
import { addWatermark as addWatermarkPage } from '@/routes/tools/pdf';
import addWatermarkTool from '@/routes/tools/pdf/add-watermark';

const { lineWithMax } = usePdfUploadLimit();
const { t } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.add_watermark.seo',
    fallbackTitleKey: 'tools.pdf.add_watermark.title',
    fallbackTitleLabel: 'Add watermark',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: addWatermarkPage.url(),
});

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.add_watermark.title',
                title: 'Add watermark',
                href: addWatermarkPage.url(),
            },
        ],
    },
});

type WmType = 'text' | 'image';
type Placement =
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'middle-left'
    | 'middle-center'
    | 'middle-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right'
    | 'mosaic';
type Layer = 'over' | 'under';
type Status = 'idle' | 'loading' | 'success' | 'error';

// ── File & preview ────────────────────────────────────────────────────────────
const file = ref<File | null>(null);
const pdfCanvas = ref<HTMLCanvasElement | null>(null);
const overlayCanvas = ref<HTMLCanvasElement | null>(null);
const {
    isLoading: previewLoading,
    error: previewError,
    pageCount,
    pageWidthPt,
    previewZoomPct,
    loadFile,
    renderPage,
    unload,
} = usePdfPage();

// ── Watermark options ─────────────────────────────────────────────────────────
const wmType = ref<WmType>('text');

// Text
const text = ref(t('tools.pdf.ui.wm_default_text_placeholder', 'CONFIDENTIAL'));
const fontFamily = ref('Helvetica');
const fontSize = ref(40);
const bold = ref(false);
const italic = ref(false);
const underline = ref(false);
const color = ref('#AAAAAA');

// Image
const wmImageFile = ref<File | null>(null);
const wmImagePreview = ref('');
const wmImageEl = ref<HTMLImageElement | null>(null);
const imageSizePct = ref(30);

// Common
const opacity = ref(0.3);
const rotation = ref(45);
const placement = ref<Placement>('middle-center');
const pageFrom = ref(1);
const pageTo = ref(0);
const usePageRange = ref(false);
const layer = ref<Layer>('over');

function formatFontPt(n: number): string {
    return `${n} pt`;
}

function formatPct(n: number): string {
    return `${n}%`;
}

function formatOpacityPct(n: number): string {
    return `${Math.round(n * 100)}%`;
}

function formatDeg(n: number): string {
    return `${n}°`;
}

// Submit status
const status = ref<Status>('idle');
const statusMessage = ref('');
const previewPage = ref(1);

// ── Preset colors ─────────────────────────────────────────────────────────────
const colorPresets = computed(() => [
    { label: t('tools.pdf.ui.color_gray', 'Gray'), hex: '#AAAAAA' },
    { label: t('tools.pdf.ui.color_black', 'Black'), hex: '#222222' },
    { label: t('tools.pdf.ui.color_red', 'Red'), hex: '#CC0000' },
    { label: t('tools.pdf.ui.color_blue', 'Blue'), hex: '#1A4FCC' },
    { label: t('tools.pdf.ui.color_green', 'Green'), hex: '#1A8C2C' },
    { label: t('tools.pdf.ui.color_gold', 'Gold'), hex: '#CC9900' },
    { label: t('tools.pdf.ui.color_white', 'White'), hex: '#FFFFFF' },
]);

const wmTypeTabs = computed(() => [
    {
        v: 'text' as WmType,
        label: t('tools.pdf.ui.watermark_text', 'Text Watermark'),
    },
    {
        v: 'image' as WmType,
        label: t('tools.pdf.ui.watermark_image', 'Image Watermark'),
    },
]);

const layerOptions = computed(() => [
    {
        v: 'over' as Layer,
        l: t('tools.pdf.ui.watermark_over_content', 'Over content'),
        desc: t(
            'tools.pdf.ui.watermark_over_content_desc',
            'Default — visible on all PDFs',
        ),
    },
    {
        v: 'under' as Layer,
        l: t('tools.pdf.ui.watermark_under_content', 'Under content'),
        desc: t(
            'tools.pdf.ui.watermark_under_content_desc',
            'Only visible on transparent PDFs',
        ),
    },
]);

function placementTitle(pos: Placement): string {
    if (pos === 'mosaic') {
        return t('tools.pdf.ui.wm_mosaic', 'Mosaic');
    }

    const k = `tools.pdf.ui.placement_${pos.replace(/-/g, '_')}`;

    return t(k, pos.replace(/-/g, ' '));
}

// ── Placement grid ────────────────────────────────────────────────────────────
const gridPositions: Placement[] = [
    'top-left',
    'top-center',
    'top-right',
    'middle-left',
    'middle-center',
    'middle-right',
    'bottom-left',
    'bottom-center',
    'bottom-right',
];
const totalPreviewPages = computed(() => Math.max(1, pageCount.value));

// ── PDF load & preview ────────────────────────────────────────────────────────
async function onFileSelected(files: File[]): Promise<void> {
    file.value = files[0] ?? null;
    status.value = 'idle';

    if (!file.value) {
        unload();

        return;
    }

    await loadFile(file.value);
    await nextTick();

    if (!pdfCanvas.value) {
        return;
    }

    await renderPreviewPage(1);
}

function removeFile(): void {
    file.value = null;
    status.value = 'idle';
    previewPage.value = 1;
    unload();
}

async function renderPreviewPage(page: number): Promise<void> {
    if (!pdfCanvas.value) {
        return;
    }

    const clamped = Math.min(totalPreviewPages.value, Math.max(1, page));
    previewPage.value = clamped;
    await renderPage(pdfCanvas.value, clamped, 440);
    await nextTick();
    drawOverlay();
}

// ── Image watermark upload ────────────────────────────────────────────────────
function onWmImageChange(e: Event): void {
    const input = e.target as HTMLInputElement;
    const f = input.files?.[0];

    if (!f) {
        return;
    }

    wmImageFile.value = f;
    const reader = new FileReader();
    reader.onload = (ev) => {
        wmImagePreview.value = ev.target?.result as string;
        const img = new Image();
        img.onload = () => {
            wmImageEl.value = img;
        };
        img.src = wmImagePreview.value;
    };
    reader.readAsDataURL(f);
}

// ── Overlay canvas drawing ────────────────────────────────────────────────────
const ALL_POSITIONS: Placement[] = [
    'top-left',
    'top-center',
    'top-right',
    'middle-left',
    'middle-center',
    'middle-right',
    'bottom-left',
    'bottom-center',
    'bottom-right',
];

function getPosPx(pos: string, pw: number, ph: number): [number, number] {
    const m = 0.08;
    const parts = pos.split('-');
    const row = parts[0];
    const col = parts[1];
    const x =
        col === 'left' ? pw * m : col === 'right' ? pw * (1 - m) : pw * 0.5;
    const y = row === 'top' ? ph * 0.1 : row === 'bottom' ? ph * 0.9 : ph * 0.5;

    return [x, y];
}

function drawOverlay(): void {
    if (!pdfCanvas.value || !overlayCanvas.value || !file.value) {
        return;
    }

    const pw = pdfCanvas.value.width;
    const ph = pdfCanvas.value.height;

    if (pw === 0 || ph === 0) {
        return;
    }

    overlayCanvas.value.width = pw;
    overlayCanvas.value.height = ph;

    const ctx = overlayCanvas.value.getContext('2d')!;
    ctx.clearRect(0, 0, pw, ph);

    // Determine which positions to render
    const positions =
        placement.value === 'mosaic' ? ALL_POSITIONS : [placement.value];

    // px per pt for font size scaling
    const pxPerPt = pageWidthPt.value > 0 ? pw / pageWidthPt.value : 1;

    for (const pos of positions) {
        const [cx, cy] = getPosPx(pos, pw, ph);
        ctx.save();
        ctx.globalAlpha = opacity.value;
        ctx.translate(cx, cy);
        ctx.rotate((rotation.value * Math.PI) / 180);

        if (wmType.value === 'text') {
            const styleStr = [
                bold.value ? 'bold' : '',
                italic.value ? 'italic' : '',
            ]
                .filter(Boolean)
                .join(' ');
            const fontMap: Record<string, string> = {
                Helvetica: 'Helvetica, Arial, sans-serif',
                Times: 'Georgia, "Times New Roman", serif',
                Courier: '"Courier New", Courier, monospace',
            };
            const canvasFontSize = Math.max(8, fontSize.value * pxPerPt);
            ctx.font = `${styleStr} ${canvasFontSize}px ${fontMap[fontFamily.value] ?? fontMap['Helvetica']}`;
            ctx.fillStyle = color.value;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const displayText =
                text.value.trim() ||
                t('tools.pdf.ui.wm_default_text_placeholder', 'CONFIDENTIAL');
            ctx.fillText(displayText, 0, 0);

            if (underline.value) {
                const m = ctx.measureText(displayText);
                const tw = m.width;
                const ly = canvasFontSize * 0.12;
                ctx.strokeStyle = color.value;
                ctx.lineWidth = Math.max(1, canvasFontSize * 0.06);
                ctx.beginPath();
                ctx.moveTo(-tw / 2, ly);
                ctx.lineTo(tw / 2, ly);
                ctx.stroke();
            }
        } else if (wmImageEl.value) {
            const imgW = pw * (imageSizePct.value / 100);
            const aspect =
                wmImageEl.value.naturalHeight / wmImageEl.value.naturalWidth;
            const imgH = imgW * aspect;
            ctx.drawImage(wmImageEl.value, -imgW / 2, -imgH / 2, imgW, imgH);
        }

        ctx.restore();
    }
}

// Re-draw overlay whenever any setting or the PDF canvas changes
watchEffect(() => {
    // Access all reactive dependencies so watchEffect tracks them
    void wmType.value;
    void text.value;
    void fontFamily.value;
    void fontSize.value;
    void bold.value;
    void italic.value;
    void underline.value;
    void color.value;
    void wmImageEl.value;
    void imageSizePct.value;
    void opacity.value;
    void rotation.value;
    void placement.value;
    void previewPage.value;
    void pageCount.value;
    void pageWidthPt.value;
    void pdfCanvas.value;
    void overlayCanvas.value;
    drawOverlay();
});

watch(previewZoomPct, () => {
    if (!file.value || !pdfCanvas.value) {
        return;
    }

    void renderPreviewPage(previewPage.value);
});

// ── Submit ────────────────────────────────────────────────────────────────────
async function extractError(err: unknown): Promise<string> {
    const e = err as { response?: { data: Blob | object } };

    if (e.response?.data instanceof Blob) {
        try {
            const parsed = JSON.parse(await (e.response.data as Blob).text());

            return (
                (Object.values(parsed.errors ?? {}).flat()[0] as string) ||
                t('tools.pdf.ui.watermark_failed_short', 'Failed.')
            );
        } catch {
            return t(
                'tools.pdf.ui.watermark_failed',
                'Failed to add watermark.',
            );
        }
    }

    return t('tools.pdf.ui.watermark_failed', 'Failed to add watermark.');
}

async function convert(): Promise<void> {
    if (
        !file.value ||
        (wmType.value === 'text' && !text.value.trim()) ||
        status.value === 'loading'
    ) {
        return;
    }

    if (wmType.value === 'image' && !wmImageFile.value) {
        return;
    }

    status.value = 'loading';
    statusMessage.value = t(
        'tools.pdf.ui.watermark_adding',
        'Adding watermark…',
    );

    const formData = new FormData();
    formData.append('file', file.value);
    formData.append('type', wmType.value);
    formData.append('placement', placement.value);
    formData.append('opacity', String(opacity.value));
    formData.append('rotation', String(rotation.value));
    formData.append('layer', layer.value);

    if (usePageRange.value) {
        formData.append('page_from', String(pageFrom.value));

        if (pageTo.value > 0) {
            formData.append('page_to', String(pageTo.value));
        }
    }

    if (wmType.value === 'text') {
        formData.append('text', text.value);
        formData.append('font', fontFamily.value);
        formData.append('font_size', String(fontSize.value));
        formData.append('color', color.value);
        formData.append('bold', bold.value ? '1' : '0');
        formData.append('italic', italic.value ? '1' : '0');
        formData.append('underline', underline.value ? '1' : '0');
    } else if (wmImageFile.value) {
        formData.append('image', wmImageFile.value);
        formData.append('image_size_pct', String(imageSizePct.value));
    }

    try {
        const response = await axios.post(
            addWatermarkTool.submit.url(),
            formData,
            { responseType: 'blob' },
        );
        const cd = (response.headers['content-disposition'] as string) ?? '';
        const filename =
            cd.match(/filename="?([^"]+)"?/)?.[1] ?? 'watermarked.pdf';
        const blobUrl = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);
        status.value = 'success';
        statusMessage.value = t(
            'tools.pdf.ui.watermark_success',
            'Watermark added successfully!',
        );
        file.value = null;
        unload();
    } catch (err) {
        status.value = 'error';
        statusMessage.value = await extractError(err);
    }
}

onUnmounted(unload);
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-add-watermark" />

    <ToolLayout
        :title="t('tools.pdf.add_watermark.title', 'Add watermark')"
        :subtitle="t('tools.pdf.add_watermark.description', '')"
        :show-how-to="false"
    >
        <!-- Layout: preview left, controls right on large screens -->
        <div class="grid gap-4 lg:grid-cols-[2fr_3fr]">
            <!-- ── Left: preview ── -->
            <div class="lg:sticky lg:top-4 lg:self-start">
                <PdfLivePreview
                    v-model:zoom="previewZoomPct"
                    :has-file="!!file"
                    :loading="previewLoading"
                    :error="previewError"
                    :current-page="previewPage"
                    :total-pages="pageCount"
                    :rendering-message="
                        t('tools.pdf.ui.rendering', 'Rendering…')
                    "
                    @change-page="renderPreviewPage"
                >
                    <!-- Base PDF page -->
                    <canvas
                        ref="pdfCanvas"
                        class="relative z-0 block max-h-none w-full bg-white dark:bg-gray-950"
                    />
                    <!-- Watermark overlay (pointer-events: none so it doesn't block interaction) -->
                    <canvas
                        ref="overlayCanvas"
                        class="absolute inset-0 z-10 h-full w-full"
                        style="pointer-events: none"
                    />
                    <template #footer>
                        <p
                            v-if="file && !previewLoading"
                            class="mt-2 text-xs text-gray-400 dark:text-gray-500"
                        >
                            {{
                                t(
                                    'tools.pdf.ui.wm_preview_footer',
                                    'Preview is approximate. Font rendering may differ slightly from the final PDF.',
                                )
                            }}
                            <span v-if="layer === 'under'">
                                {{
                                    t(
                                        'tools.pdf.ui.wm_preview_layer_under',
                                        'Layer "under content" is shown as "over" in this preview.',
                                    )
                                }}</span
                            >
                        </p>
                    </template>
                </PdfLivePreview>
                <div class="mt-4">
                    <ToolHowToCard />
                </div>
            </div>

            <!-- ── Right: controls ── -->
            <div class="space-y-4">
                <!-- File upload -->
                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                    <DropZone
                        accept="application/pdf"
                        :multiple="false"
                        :label="
                            t(
                                'tools.pdf.ui.drop_pdf',
                                'Click or drag a PDF here',
                            )
                        "
                        :sublabel="
                            lineWithMax(
                                t('tools.pdf.ui.one_pdf_file', 'One PDF file'),
                            )
                        "
                        @update:model-value="onFileSelected"
                    />
                    <div v-if="file" class="mt-3">
                        <FileCard
                            :file="file"
                            :show-drag-handle="false"
                            @remove="removeFile"
                        />
                    </div>
                </div>

                <!-- Type tabs -->
                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                    <div
                        class="mb-4 flex rounded-lg border border-gray-200 p-1 dark:border-gray-700"
                    >
                        <button
                            v-for="wmTab in wmTypeTabs"
                            :key="wmTab.v"
                            type="button"
                            class="flex-1 rounded-md py-1.5 text-sm font-medium transition-colors"
                            :class="
                                wmType === wmTab.v
                                    ? 'bg-brand-600 text-white'
                                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
                            "
                            @click="wmType = wmTab.v"
                        >
                            {{ wmTab.label }}
                        </button>
                    </div>

                    <!-- Text options -->
                    <div v-if="wmType === 'text'" class="space-y-4">
                        <div>
                            <label
                                class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400"
                                >{{
                                    t(
                                        'tools.pdf.ui.wm_watermark_text_label',
                                        'Watermark text',
                                    )
                                }}</label
                            >
                            <input
                                v-model="text"
                                type="text"
                                maxlength="100"
                                :placeholder="
                                    t(
                                        'tools.pdf.ui.wm_default_text_placeholder',
                                        'CONFIDENTIAL',
                                    )
                                "
                                class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500 focus:outline-none dark:border-border dark:bg-muted dark:text-foreground"
                            />
                        </div>

                        <!-- Font family -->
                        <div>
                            <label
                                class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400"
                                >{{ t('tools.pdf.ui.font', 'Font') }}</label
                            >
                            <div class="flex gap-2">
                                <button
                                    v-for="f in [
                                        'Helvetica',
                                        'Times',
                                        'Courier',
                                    ]"
                                    :key="f"
                                    type="button"
                                    class="flex-1 rounded-lg border py-1.5 text-sm transition-colors"
                                    :class="
                                        fontFamily === f
                                            ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                                            : 'border-gray-200 text-gray-700 hover:border-brand-300 dark:border-gray-700 dark:text-gray-300'
                                    "
                                    @click="fontFamily = f"
                                >
                                    {{ f }}
                                </button>
                            </div>
                        </div>

                        <!-- Style + size -->
                        <div class="flex items-end gap-3">
                            <div>
                                <label
                                    class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400"
                                    >{{
                                        t(
                                            'tools.pdf.ui.wm_style_label',
                                            'Style',
                                        )
                                    }}</label
                                >
                                <div class="flex gap-1">
                                    <button
                                        type="button"
                                        class="h-9 w-9 rounded-lg border text-sm font-bold transition-colors"
                                        :class="
                                            bold
                                                ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950'
                                                : 'border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300'
                                        "
                                        @click="bold = !bold"
                                    >
                                        B
                                    </button>
                                    <button
                                        type="button"
                                        class="h-9 w-9 rounded-lg border text-sm italic transition-colors"
                                        :class="
                                            italic
                                                ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950'
                                                : 'border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300'
                                        "
                                        @click="italic = !italic"
                                    >
                                        I
                                    </button>
                                    <button
                                        type="button"
                                        class="h-9 w-9 rounded-lg border text-sm underline transition-colors"
                                        :class="
                                            underline
                                                ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950'
                                                : 'border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300'
                                        "
                                        @click="underline = !underline"
                                    >
                                        U
                                    </button>
                                </div>
                            </div>
                            <div class="flex-1">
                                <ToolSliderField
                                    v-model="fontSize"
                                    :label="t('tools.pdf.ui.size', 'Size')"
                                    :min="12"
                                    :max="96"
                                    :format-value="formatFontPt"
                                />
                            </div>
                        </div>

                        <!-- Color -->
                        <div>
                            <label
                                class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400"
                                >{{
                                    t('tools.pdf.ui.pn_color_label', 'Color')
                                }}</label
                            >
                            <div class="flex flex-wrap items-center gap-1.5">
                                <button
                                    v-for="p in colorPresets"
                                    :key="p.hex"
                                    type="button"
                                    :title="p.label"
                                    :aria-label="
                                        t(
                                            'tools.pdf.ui.color_preset_aria',
                                            'Color {label}',
                                        ).replace('{label}', p.label)
                                    "
                                    class="h-7 w-7 rounded-full border-2 transition-transform hover:scale-110"
                                    :style="{ backgroundColor: p.hex }"
                                    :class="
                                        color === p.hex
                                            ? 'scale-110 border-brand-600'
                                            : 'border-gray-300 dark:border-gray-600'
                                    "
                                    @click="color = p.hex"
                                />
                                <input
                                    v-model="color"
                                    type="color"
                                    :title="
                                        t(
                                            'tools.pdf.ui.custom_color',
                                            'Custom color',
                                        )
                                    "
                                    class="h-7 w-7 cursor-pointer rounded-full border-2 border-gray-300 p-0"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Image options -->
                    <div v-else class="space-y-4">
                        <label
                            class="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 p-5 text-center hover:border-brand-300 dark:border-gray-700"
                        >
                            <span
                                class="text-sm text-gray-500 dark:text-gray-400"
                                >{{
                                    t(
                                        'tools.pdf.ui.wm_image_drop_title',
                                        'Click to upload watermark image',
                                    )
                                }}</span
                            >
                            <span class="mt-1 text-xs text-gray-400">{{
                                t(
                                    'tools.pdf.ui.wm_image_drop_hint',
                                    'PNG, JPG, SVG with transparency recommended',
                                )
                            }}</span>
                            <input
                                type="file"
                                accept="image/*"
                                class="hidden"
                                @change="onWmImageChange"
                            />
                        </label>
                        <img
                            v-if="wmImagePreview"
                            :src="wmImagePreview"
                            :alt="
                                t(
                                    'tools.pdf.ui.wm_image_alt',
                                    'Watermark preview',
                                )
                            "
                            class="mx-auto max-h-24 rounded border border-gray-200 object-contain dark:border-gray-700"
                        />
                        <div>
                            <ToolSliderField
                                v-model="imageSizePct"
                                :label="
                                    t('tools.pdf.ui.image_size', 'Image size')
                                "
                                :min="10"
                                :max="80"
                                :format-value="formatPct"
                            />
                            <p class="mt-1 text-xs text-muted-foreground">
                                {{
                                    t(
                                        'tools.pdf.ui.pct_of_page_width',
                                        '% of page width',
                                    )
                                }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Common: opacity + rotation -->
                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                    <div class="grid grid-cols-2 gap-4">
                        <ToolSliderField
                            v-model="opacity"
                            :label="t('tools.pdf.ui.opacity', 'Opacity')"
                            :min="0.05"
                            :max="1"
                            :step="0.05"
                            :format-value="formatOpacityPct"
                        />
                        <ToolSliderField
                            v-model="rotation"
                            :label="t('tools.pdf.ui.rotation', 'Rotation')"
                            :min="0"
                            :max="360"
                            :format-value="formatDeg"
                        />
                    </div>
                </div>

                <!-- Placement grid -->
                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                    <p
                        class="mb-3 text-xs font-medium text-gray-600 dark:text-gray-400"
                    >
                        {{ t('tools.pdf.ui.wm_placement', 'Placement') }}
                    </p>
                    <div class="flex items-center gap-4">
                        <div class="grid grid-cols-3 gap-1.5">
                            <button
                                v-for="pos in gridPositions"
                                :key="pos"
                                type="button"
                                :title="placementTitle(pos)"
                                :aria-label="
                                    t(
                                        'tools.pdf.ui.pn_position_aria',
                                        'Position {label}',
                                    ).replace('{label}', placementTitle(pos))
                                "
                                class="h-9 w-9 rounded-md border-2 transition-colors"
                                :class="
                                    placement === pos
                                        ? 'border-brand-600 bg-brand-500'
                                        : 'border-gray-300 bg-gray-100 hover:border-brand-400 hover:bg-brand-50 dark:border-gray-600 dark:bg-gray-700'
                                "
                                @click="placement = pos"
                            />
                        </div>
                        <button
                            type="button"
                            class="rounded-lg border px-3 py-2 text-sm font-medium transition-colors"
                            :class="
                                placement === 'mosaic'
                                    ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                                    : 'border-gray-200 text-gray-700 hover:border-brand-300 dark:border-gray-700 dark:text-gray-300'
                            "
                            @click="placement = 'mosaic'"
                        >
                            {{ t('tools.pdf.ui.wm_mosaic', 'Mosaic') }}<br />
                            <span class="text-xs text-gray-400">{{
                                t(
                                    'tools.pdf.ui.wm_mosaic_sub',
                                    'all 9 positions',
                                )
                            }}</span>
                        </button>
                    </div>
                    <p class="mt-2 text-xs text-gray-400 dark:text-gray-500">
                        {{ t('tools.pdf.ui.wm_selected', 'Selected:') }}
                        <span
                            class="font-medium text-gray-600 dark:text-gray-300"
                            >{{ placementTitle(placement) }}</span
                        >
                    </p>
                </div>

                <!-- Page range -->
                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                    <label
                        class="flex cursor-pointer items-start gap-3.5 select-none"
                    >
                        <Checkbox
                            v-model="usePageRange"
                            class="mt-0.5 shrink-0"
                        />
                        <span
                            class="text-sm leading-snug text-gray-700 dark:text-gray-300"
                        >
                            {{
                                t(
                                    'tools.pdf.ui.wm_apply_page_range_only',
                                    'Apply to specific page range only',
                                )
                            }}
                        </span>
                    </label>
                    <div v-if="usePageRange" class="mt-3 flex gap-3">
                        <div class="flex-1">
                            <label class="mb-1 block text-xs text-gray-500">{{
                                t('tools.pdf.ui.wm_from_page', 'From page')
                            }}</label>
                            <input
                                v-model.number="pageFrom"
                                type="number"
                                min="1"
                                class="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none dark:border-border dark:bg-muted dark:text-foreground"
                            />
                        </div>
                        <div class="flex-1">
                            <label class="mb-1 block text-xs text-gray-500">{{
                                t(
                                    'tools.pdf.ui.wm_to_page',
                                    'To page (0 = last)',
                                )
                            }}</label>
                            <input
                                v-model.number="pageTo"
                                type="number"
                                min="0"
                                class="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none dark:border-border dark:bg-muted dark:text-foreground"
                            />
                        </div>
                    </div>
                </div>

                <!-- Layer -->
                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                    <p
                        class="mb-2 text-xs font-medium text-gray-600 dark:text-gray-400"
                    >
                        {{ t('tools.pdf.ui.wm_layer_title', 'Layer') }}
                    </p>
                    <div class="flex gap-2">
                        <button
                            v-for="opt in layerOptions"
                            :key="opt.v"
                            type="button"
                            class="flex-1 rounded-lg border px-3 py-2 text-sm transition-colors"
                            :class="
                                layer === opt.v
                                    ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                                    : 'border-gray-200 text-gray-700 hover:border-brand-300 dark:border-gray-700 dark:text-gray-300'
                            "
                            @click="layer = opt.v"
                        >
                            <span class="block font-medium">{{ opt.l }}</span>
                            <span class="text-xs text-gray-400">{{
                                opt.desc
                            }}</span>
                        </button>
                    </div>
                </div>

                <Button
                    class="w-full bg-brand-600 text-white hover:bg-brand-700"
                    :disabled="
                        !file ||
                        (wmType === 'text' && !text.trim()) ||
                        (wmType === 'image' && !wmImageFile) ||
                        status === 'loading'
                    "
                    @click="convert"
                >
                    {{ t('tools.pdf.ui.wm_add_button', 'Add Watermark') }}
                </Button>

                <StatusMessage :status="status" :message="statusMessage" />
            </div>
        </div>
    </ToolLayout>
</template>
